/**
 * The full detail behind an assistant run's steps, kept for this page session
 * only (docs/agent.md §4).
 *
 * The split is deliberate. The *compact* record — the tool, its arguments, and
 * the exact archive request — goes into the CRDT, so it survives a reload and
 * collaborators can see it. The bulky parts kept here — raw response bodies, the
 * whole `ToolResult`, and the post-`resultBudget` string handed to the model —
 * would multiply the size of every project document for something only the
 * person debugging the run wants. They are recoverable by re-running; the query
 * that produced them is not.
 *
 * So an inspector opened on a reloaded page shows the request but not the
 * response, and says so rather than rendering an empty pane.
 */
import type { PandoraWireCall } from '../api/pandora';
import type { Op, ToolKind, ToolResult } from '../agent/core/types';

/** One tool call, with everything observable about it. */
export interface StepRecord {
  /** Stable identity within a run. NOT the provider's tool-call id — see below. */
  seq: number;
  /**
   * The provider's id, for display only. llm.ts falls back to `call_${index}`
   * per stream, so it can repeat across steps of the same run.
   */
  toolCallId: string;
  tool: string;
  kind: ToolKind | 'unknown';
  label: string;
  args: Record<string, unknown>;
  /** Verbatim from the model, so a malformed call is still legible. */
  rawArgs: string;
  state: 'running' | 'ok' | 'error';
  error?: string;
  ms?: number;
  /** Archive requests made while this step was in flight. */
  calls: PandoraWireCall[];
  /** Mutate tools: the resolved arguments recorded in the op log. */
  opArgs?: Record<string, unknown>;
  /** `propose`: the op log it froze. */
  ops?: Op[];
  /** Exactly what went back to the model — already clipped, so maybe not JSON. */
  content?: string;
  result?: ToolResult;
}

/**
 * Runs held at once. Small on purpose: this is a debugging aid for the run in
 * front of you, not an archive, and raw transcript responses are large.
 */
export const MAX_SESSION_RUNS = 8;

const runs = new Map<string, StepRecord[]>();

export function rememberRun(runId: string, steps: StepRecord[]): void {
  if (!runId) return;
  // Re-inserting moves it to the end, so the eviction order is by last write.
  runs.delete(runId);
  runs.set(runId, steps);
  while (runs.size > MAX_SESSION_RUNS) {
    const oldest = runs.keys().next();
    if (oldest.done) break;
    runs.delete(oldest.value);
  }
}

export function recallRun(runId: string): StepRecord[] | null {
  return runs.get(runId) ?? null;
}

/** Dropped on project switch: the next document is a different room. */
export function clearSessionTraces(): void {
  runs.clear();
}
